<script>
    // Pagina publica de eliminacion de cuenta y datos.
    //
    // Google Play la exige como "URL de eliminacion de cuenta" (Play Console ->
    // Contenido de la app -> Eliminacion de cuentas y datos) y le pide tres
    // cosas concretas, que son las tres secciones de abajo:
    //   1. hacer referencia al nombre de la app o del desarrollador,
    //   2. destacar los pasos para solicitar el borrado,
    //   3. especificar que datos se borran o se conservan, y los periodos
    //      de retencion.
    // Como /privacidad, NO usa Navbar2 ni nada que dependa de la sesion: el
    // revisor la abre sin login.
    //
    // El contenido describe lo que AccountDeletionService hace de verdad
    // (borrado real desde la V42; antes era un is_active = 0). Si cambia esa
    // logica hay que actualizar esta pagina, /privacidad y la declaracion de
    // Data Safety: Google compara las tres.
    const actualizado = "30 de agosto de 2026";
</script>

<svelte:head>
    <title>Eliminar tu cuenta · Creciente Fértil</title>
    <meta
        name="description"
        content="Cómo eliminar tu cuenta de Creciente Fértil y qué datos se borran o se conservan."
    />
</svelte:head>

<div class="min-h-screen bg-cf-bg py-10 px-4">
    <div class="mx-auto max-w-3xl bg-cf-surface rounded-xl shadow-lg p-6 sm:p-10">
        <h1 class="text-3xl font-bold text-cf-primary mb-1">Eliminar tu cuenta</h1>
        <p class="text-sm text-cf-text-sub mb-8">
            Última actualización: {actualizado}
        </p>

        <div class="space-y-8 text-cf-text leading-relaxed">
            <section class="space-y-2">
                <p>
                    Esta página explica cómo eliminar tu cuenta de la aplicación
                    <strong>Creciente Fértil</strong> y qué pasa con tu información cuando lo hacés.
                    Vale tanto para la aplicación móvil como para la versión web.
                </p>
            </section>

            <section class="space-y-3">
                <h2 class="text-xl font-semibold text-cf-primary">Cómo eliminarla</h2>
                <p>Podés hacerlo vos mismo desde la aplicación, sin pedírselo a nadie:</p>
                <ol class="list-decimal pl-6 space-y-1">
                    <li>Entrá a la aplicación con tu cuenta.</li>
                    <li>Abrí el menú y entrá en <strong>Configuración de la cuenta</strong>.</li>
                    <li>Al final de la pantalla, tocá <strong>Eliminar cuenta</strong>.</li>
                    <li>Confirmá tocando <strong>Eliminar</strong> en el aviso que aparece.</li>
                </ol>
                <p class="rounded-lg bg-cf-surface-alt p-3 text-sm">
                    El borrado es <strong>inmediato y no se puede deshacer</strong>. No hay un
                    período de gracia para arrepentirse, así que conviene exportar a Excel lo que
                    quieras conservar antes de eliminar la cuenta.
                </p>
                <p>
                    Si no llegás a entrar a la aplicación, escribinos a
                    <strong>crecientefertil1@gmail.com</strong> desde la dirección de correo con la
                    que te registraste y lo hacemos por vos.
                </p>
            </section>

            <section class="space-y-3">
                <h2 class="text-xl font-semibold text-cf-primary">Qué se borra</h2>
                <ul class="list-disc pl-6 space-y-1">
                    <li>
                        <strong>Tu cuenta:</strong> nombre, correo electrónico, teléfono y
                        contraseña. Tu correo queda libre, así que podés volver a registrarte con
                        el mismo si algún día querés.
                    </li>
                    <li>
                        <strong>Tus establecimientos, si sos la única persona en ellos:</strong> se
                        borran con todos sus animales, pesajes, tactos, servicios, nacimientos,
                        tratamientos, movimientos, lotes y rodeos.
                    </li>
                    <li>
                        <strong>Tus sesiones abiertas</strong> en todos los dispositivos, y la copia
                        de tus datos que la aplicación guarda en el teléfono para funcionar sin
                        conexión.
                    </li>
                </ul>
            </section>

            <section class="space-y-3">
                <h2 class="text-xl font-semibold text-cf-primary">Qué se conserva, y por qué</h2>
                <ul class="list-disc pl-6 space-y-1">
                    <li>
                        <strong>Los establecimientos donde trabajan otras personas.</strong> Si
                        compartís un establecimiento con colaboradores, ese establecimiento y sus
                        datos siguen siendo de ellos: lo que se borra es tu acceso. Los datos de la
                        explotación no son datos personales tuyos.
                    </li>
                    <li>
                        <strong>Los animales que hayas transferido a otro establecimiento.</strong>
                        Ya pertenecen al campo que los recibió, junto con el registro de esa
                        transferencia.
                    </li>
                    <li>
                        <strong>El historial de pagos de tu suscripción</strong>, sin quedar
                        asociado a vos. Se conserva por el plazo que exige la normativa contable y
                        fiscal argentina, y ya no permite identificarte.
                    </li>
                    <li>
                        <strong>Un registro de que la cuenta se eliminó</strong>, con la fecha. Es
                        la constancia de que el borrado ocurrió.
                    </li>
                </ul>
            </section>

            <section class="space-y-2">
                <h2 class="text-xl font-semibold text-cf-primary">
                    Si sos el único administrador de un establecimiento
                </h2>
                <p>
                    Cuando sos la única persona con permisos de administrador en un establecimiento
                    donde hay colaboradores trabajando, la aplicación <strong>no te deja eliminar
                    la cuenta todavía</strong> y te dice cuál es ese establecimiento. Si te fueras,
                    quedaría un campo que nadie puede administrar. Para poder eliminarla, nombrá
                    antes a otro administrador o sacá a los colaboradores.
                </p>
            </section>

            <section class="space-y-2">
                <h2 class="text-xl font-semibold text-cf-primary">Borrar sólo una parte</h2>
                <p>
                    No hace falta eliminar la cuenta para borrar información. Desde la aplicación
                    podés borrar cualquier registro que hayas cargado. Y si querés que borremos
                    algún dato personal tuyo sin dar de baja la cuenta, escribinos a
                    <strong>crecientefertil1@gmail.com</strong> y te respondemos.
                </p>
            </section>

            <section class="space-y-2">
                <h2 class="text-xl font-semibold text-cf-primary">Más información</h2>
                <p>
                    Qué datos recolectamos y con qué finalidad está explicado en nuestra
                    <a class="text-cf-primary underline" href="/privacidad">política de privacidad</a>.
                </p>
            </section>
        </div>
    </div>
</div>
